/**
 * OC FAB Widget Component
 * Floating Action Button widget with sliding chat panel
 * Handles authentication, panel toggle, and session management
 */

import { Alert } from '@hl/ui-components';
import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';

import { OcChatConfig } from '../../types/oc';
import { OcFabWidgetOptions } from '../../types/oc';
import { OcSkeletonDefs } from '../Loading/SkeletonSVG';
import { ErrorWrapper } from '../TranscriptBox/TranscriptMessages.styled';
import {
  AuthState,
  OcChatPanel,
} from './components/ChatPanelContent/OcChatPanel';
import OcFabButton from './components/FabButton/OcFabButton';
import { NotificationProvider } from './components/Notification/NotificationProvider';
import {
  ConfigValidationError,
  validateOcChatConfig,
} from './config/configValidator';
import { getOcTheme, IOcAppTheme } from './config/ocTheme';
import {
  OcFabWidgetOptionsProvider,
} from './contexts/OcFabWidgetOptionsContext';
import { hasAuthHeaders } from './services/ocApiClient';
import { checkOcAuth } from './services/ocEndpoints';
import { SessionManagerProvider } from './sessionManagement/SessionManagerContext';
import { OcGlobalStyleWrapper } from './styles/OcGlobalStyle';

export interface OcFabWidgetProps {
  config: OcChatConfig;
  options?: OcFabWidgetOptions;
}

/**
 * OcFabWidget Component
 * Main FAB widget with authentication and session management
 */
export const OcFabWidget: FC<OcFabWidgetProps> = ({ config, options }) => {
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [userId, setUserId] = useState<string>('');
  const [error, setError] = useState<Error | null>(null);
  const [isChatOpen, setIsChatOpen] = useState(options?.autoOpen ?? false);
  const hasChatBeenOpen = useRef(options?.autoOpen ?? false);
  const originToolId =
    config?.originToolId ||
    config?.aiAssistantKey ||
    '00000000-0000-0000-0000-000000000000';

  // Determine if we're in preview mode
  const isPreviewMode = config.mode === 'preview';
  const previewState = options?.previewState;

  // Memoize config to prevent unnecessary re-renders and API calls
  // Only recreate if actual config values change
  const stableConfig = useMemo(
    () => config,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      config.apiBaseUrl,
      config.tenantId,
      config.aiAssistantKey,
      config.loginUrl,
      config.originToolId,
      config.mode,
    ],
  );

  // Check authentication on mount (skip in preview mode)
  useEffect(() => {
    // In preview mode, set auth state based on previewState
    if (isPreviewMode) {
      if (!previewState) {
        const error = new Error('Preview mode requires previewState to be provided');
        setError(error);
        setAuthState('error');
        if (options?.onError) {
          options.onError(error);
        }
        return;
      }

      // Set auth state based on previewState.authState
      if (previewState.authState === 'authenticated') {
        setUserId('preview-user-id');
        setAuthState('authenticated');
        if (options?.onReady) {
          setTimeout(options.onReady, 0);
        }
      } else {
        setAuthState('unauthenticated');
        if (options?.onAuthRequired) {
          options.onAuthRequired();
        }
      }
      return;
    }

    // Production mode - check authentication normally
    const checkAuth = async () => {
      try {
        validateOcChatConfig(stableConfig);

        // Only check authentication if headers are available
        // If no iamKey and no cookies, skip the API call and set unauthenticated state
        if (!hasAuthHeaders(stableConfig)) {
          setAuthState('unauthenticated');
          if (options?.onAuthRequired) {
            options.onAuthRequired();
          }
          return;
        }

        const authResponse = await checkOcAuth(stableConfig);

        if (authResponse.isAuthenticated) {
          setUserId(authResponse.userId || '');
          setAuthState('authenticated');

          if (options?.onReady) {
            setTimeout(options.onReady, 0);
          }
        } else {
          setAuthState('unauthenticated');

          if (options?.onAuthRequired) {
            options.onAuthRequired();
          }
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setError(error);
        setAuthState('error');

        if (options?.onError) {
          options.onError(error);
        }
      }
    };

    checkAuth();
  }, [stableConfig, options, isPreviewMode, previewState]);

  // Build theme based on themeMode and useSVGBackground
  const themeMode = options?.themeMode || 'light';
  const useSVGBackground = options?.useSVGBackground !== false; // default true
  const baseTheme = getOcTheme(themeMode, useSVGBackground);

  const theme: IOcAppTheme = options?.theme
    ? {
      ...baseTheme,
      color: {
        ...baseTheme.color,
        ...options.theme.color,
      },
      font: {
        size: {
          ...baseTheme.font.size,
          ...options.theme.font?.size,
        },
        weight: {
          ...baseTheme.font.weight,
          ...options.theme.font?.weight,
        },
      },
    }
    : baseTheme;

  // Handle FAB click
  const handleFabClick = () => {
    const newState = !isChatOpen;
    setIsChatOpen(newState);
    hasChatBeenOpen.current = true;

    if (options?.onFabClick) {
      options.onFabClick();
    }

    if (options?.onPanelToggle) {
      options.onPanelToggle(newState);
    }
  };

  // Handle panel close
  const handlePanelClose = () => {
    setIsChatOpen(false);

    if (options?.onPanelToggle) {
      options.onPanelToggle(false);
    }
  };

  // Error state (configuration errors only - don't show FAB)
  if (
    authState === 'error' &&
    error &&
    error instanceof ConfigValidationError
  ) {
    const errorMessage = `Configuration Error: ${error.message}`;

    return (
      <>
        <OcGlobalStyleWrapper />
        <ThemeProvider theme={theme}>
          <OcSkeletonDefs />
          <ErrorWrapper>
            {/* @ts-expect-error HL components is on an older react version where children is implicitly included */}
            <Alert danger={true}>{errorMessage}</Alert>
          </ErrorWrapper>
        </ThemeProvider>
      </>
    );
  }

  return (
    <>
      <OcGlobalStyleWrapper />
      <ThemeProvider theme={theme}>
        <OcSkeletonDefs />
        <OcFabWidgetOptionsProvider options={options}>
          <NotificationProvider>
            {/* FAB Button - always visible (even when checking/unauthenticated) */}
            <OcFabButton
              onClick={handleFabClick}
            />

            {/* Chat Panel - lazy loaded after first open */}
            {hasChatBeenOpen.current && (
              <SessionManagerProvider
                config={stableConfig}
                originToolId={originToolId}
                isAuthenticated={authState === 'authenticated'}
                previewState={previewState}
              >
                <OcChatPanel
                  config={stableConfig}
                  userId={userId}
                  authState={authState}
                  error={error}
                  theme={theme}
                  isOpen={isChatOpen}
                  onClose={handlePanelClose}
                />
              </SessionManagerProvider>
            )}
          </NotificationProvider>
        </OcFabWidgetOptionsProvider>
      </ThemeProvider>
    </>
  );
};

export default OcFabWidget;
